"use client";

import type React from "react";
import { useLayoutEffect, useRef } from "react";
import { cn } from "@/lib/utils";

const PADDING = 8; // px gap from viewport edge

interface ShiftIntoViewProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * Wraps positioned content (dropdowns, popovers) and shifts it horizontally
 * so it stays within the viewport. Uses translateX — does not affect layout
 * box, so this is only suitable for absolutely/fixed positioned content that
 * has no in-flow siblings.
 */
export function ShiftIntoView({ children, className }: ShiftIntoViewProps) {
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    function update() {
      if (!el) return;
      // Reset before measuring so we don't compound corrections
      el.style.transform = "";

      const rect = el.getBoundingClientRect();
      const viewportWidth = window.innerWidth;

      const overflowRight = rect.right - viewportWidth;
      const overflowLeft = -rect.left;

      if (overflowRight > 0) {
        el.style.transform = `translateX(-${overflowRight + PADDING}px)`;
      } else if (overflowLeft > 0) {
        el.style.transform = `translateX(${overflowLeft + PADDING}px)`;
      }
    }

    update();

    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("resize", update, { passive: true });
    window.addEventListener("scroll", update, { passive: true, capture: true });

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, { capture: true });
    };
  }, []);

  return (
    <div ref={ref} className={cn("w-fit", className)}>
      {children}
    </div>
  );
}
